import type { ProfileWalkInDto, ProfileWalkInsResponse } from '@/types/dto/walk-in.dto';
import type { ProfileWalkIn } from '@/types/profile';

export type ProfileWalkInDisplayState = 'called' | 'cancelled' | 'completed' | 'waiting';

function normalizeText(value?: string | null) {
  const normalizedValue = value?.trim();

  return normalizedValue ? normalizedValue : null;
}

function normalizeNumber(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getTimestamp(value?: string | null) {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  const timestamp = new Date(normalizedValue).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
}

function getStatusFingerprint(status?: string | null) {
  return normalizeText(status)?.toUpperCase() ?? '';
}

function getWalkInsFromResponse(response?: ProfileWalkInsResponse | null) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
}

function normalizeProfileWalkIn(walkIn: ProfileWalkInDto): ProfileWalkIn | null {
  const normalizedId = normalizeText(walkIn.id);

  if (!normalizedId) {
    return null;
  }

  return {
    companyId: normalizeText(walkIn.companyId ?? walkIn.company?.id),
    companyName: normalizeText(walkIn.companyName ?? walkIn.company?.name) ?? 'Local da fila',
    companyPhoto: normalizeText(walkIn.companyPhoto ?? walkIn.company?.profilePhoto ?? walkIn.company?.coverPhoto),
    companySlug: normalizeText(walkIn.companySlug ?? walkIn.company?.slug),
    createdAt: normalizeText(walkIn.createdAt) ?? new Date().toISOString(),
    id: normalizedId,
    position: normalizeNumber(walkIn.position ?? walkIn.queuePosition ?? walkIn.currentPosition),
    protocol: normalizeText(walkIn.protocol),
    status: normalizeText(walkIn.status),
  };
}

export function getProfileWalkInDisplayState(walkIn: Pick<ProfileWalkIn, 'status'>): ProfileWalkInDisplayState {
  const normalizedStatus = getStatusFingerprint(walkIn.status);

  if (normalizedStatus.includes('CANCEL')) {
    return 'cancelled';
  }

  if (
    normalizedStatus.includes('COMPLETE') ||
    normalizedStatus.includes('ATTEND') ||
    normalizedStatus.includes('FINISH') ||
    normalizedStatus.includes('DONE') ||
    normalizedStatus.includes('CLOSE')
  ) {
    return 'completed';
  }

  if (
    normalizedStatus.includes('CALL') ||
    normalizedStatus.includes('READY') ||
    normalizedStatus.includes('START') ||
    normalizedStatus.includes('SERVICE')
  ) {
    return 'called';
  }

  return 'waiting';
}

function isActiveProfileWalkIn(walkIn: Pick<ProfileWalkIn, 'status'>) {
  const displayState = getProfileWalkInDisplayState(walkIn);

  return displayState === 'waiting' || displayState === 'called';
}

export function canCancelProfileWalkIn(walkIn: Pick<ProfileWalkIn, 'status'>) {
  return isActiveProfileWalkIn(walkIn);
}

export function getProfileWalkInStatusLabel(walkIn: Pick<ProfileWalkIn, 'status'>) {
  switch (getProfileWalkInDisplayState(walkIn)) {
    case 'called':
      return 'Chamando';
    case 'cancelled':
      return 'Cancelado';
    case 'completed':
      return 'Atendido';
    default:
      return 'Na fila';
  }
}

function sortProfileWalkIns(walkIns: ProfileWalkIn[], section: 'active' | 'history') {
  return walkIns.toSorted((left, right) => {
    const leftTime = getTimestamp(left.createdAt) ?? (section === 'active' ? Number.MAX_SAFE_INTEGER : 0);
    const rightTime = getTimestamp(right.createdAt) ?? (section === 'active' ? Number.MAX_SAFE_INTEGER : 0);

    return section === 'active' ? leftTime - rightTime : rightTime - leftTime;
  });
}

export function normalizeProfileWalkInsResponse(response?: ProfileWalkInsResponse | null) {
  const walkIns = getWalkInsFromResponse(response).flatMap((walkIn) => {
    const normalizedWalkIn = normalizeProfileWalkIn(walkIn);

    return normalizedWalkIn ? [normalizedWalkIn] : [];
  });
  const activeWalkIns = sortProfileWalkIns(
    walkIns.filter((walkIn) => isActiveProfileWalkIn(walkIn)),
    'active',
  );
  const historyWalkIns = sortProfileWalkIns(
    walkIns.filter((walkIn) => !isActiveProfileWalkIn(walkIn)),
    'history',
  );

  return {
    activeWalkIns,
    counters: {
      active: activeWalkIns.length,
      history: historyWalkIns.length,
    },
    historyWalkIns,
    walkIns: [...activeWalkIns, ...historyWalkIns],
  };
}
